import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, Filter, Search, LogIn, AlertTriangle, Database, RefreshCw, ArrowUp, ArrowDown } from 'lucide-react';
import { GameCard } from '../components/GameCard';
import {
  getPagedUserCollection,
  toggleOwned,
  toggleWishlist,
  removeFromCollection,
  togglePlayed,
  updatePlayCount,
  CollectionFilters,
  CollectionSort,
  Pagination,
  UserGame,
  Game
} from '../lib/game/collectionService';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import useDebounce from '../hooks/useDebounce';
import SEO from '../components/SEO';

const PAGE_SIZE = 20;

const WishlistPage: React.FC = () => {
  const { user, isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [pagedWishlist, setPagedWishlist] = useState<UserGame[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [diagnosticInfo, setDiagnosticInfo] = useState<string | null>(null);

  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState<CollectionSort>({ sortBy: 'game_name', ascending: true });
  const [currentPage, setCurrentPage] = useState(1);

  const debouncedSearchText = useDebounce(searchText, 500);

  const loadPagedWishlist = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const filters: CollectionFilters = {
        type: 'wishlist',
        searchText: debouncedSearchText || null,
      };
      const pagination: Pagination = {
        page: currentPage,
        pageSize: PAGE_SIZE,
      };

      const result = await getPagedUserCollection(user.id, filters, sortConfig, pagination);

      setPagedWishlist(result.data || []);
      setTotalCount(result.count || 0);

      if (isAdmin) {
        setDiagnosticInfo(`Wishlist carregada. UserId: ${user.id}, Página: ${currentPage}, Filtros: ${JSON.stringify(filters)}, Ordenação: ${JSON.stringify(sortConfig)}, Total: ${result.count}`);
      }

    } catch (error) {
      setError(`Não foi possível carregar sua wishlist: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      if (isAdmin) {
        setDiagnosticInfo(`Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      }
      setPagedWishlist([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [user?.id, debouncedSearchText, sortConfig, currentPage, isAdmin]);

  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      loadPagedWishlist();
    } else if (!authLoading && !isAuthenticated) {
      setPagedWishlist([]);
      setTotalCount(0);
      setCurrentPage(1);
      setLoading(false);
    }
  }, [user, isAuthenticated, authLoading, debouncedSearchText, sortConfig, currentPage, loadPagedWishlist]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (newSortByValue: string) => {
    const newSortBy = newSortByValue as CollectionSort['sortBy'];
    setSortConfig(prevSort => {
      const isAscending = prevSort.sortBy === newSortBy ? !prevSort.ascending : true;
      return { sortBy: newSortBy, ascending: isAscending };
    });
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleToggleOwned = async (gameId: string, isOwned: boolean) => {
    if (!user?.id) return;
    try {
      await toggleOwned(user.id, gameId, isOwned);
      loadPagedWishlist();
    } catch (error) {
      console.error('Erro ao atualizar owned:', error);
      setError('Não foi possível atualizar o jogo. Tente novamente.');
    }
  };

  const handleToggleWishlist = async (gameId: string, isWishlist: boolean) => {
    if (!user?.id) return;
    try {
      await toggleWishlist(user.id, gameId, isWishlist);
      loadPagedWishlist();
    } catch (error) {
      console.error('Erro ao atualizar wishlist:', error);
      setError('Não foi possível atualizar a wishlist. Tente novamente.');
    }
  };

  const handleTogglePlayed = async (gameId: string, isPlayed: boolean) => {
    if (!user?.id) return;
    
    try {
      setError(null); // Limpar qualquer erro anterior
      console.log(`[WishlistPage] Tentando marcar jogo ${gameId} como ${isPlayed ? 'jogado' : 'não jogado'}`);
      
      const updatedUserGame = await togglePlayed(user.id, gameId, isPlayed);
      console.log('[WishlistPage] Jogo atualizado com sucesso:', updatedUserGame);
      
      // Atualizar a UI com os dados atualizados
      await loadPagedWishlist();
    } catch (error) {
      console.error('[WishlistPage] Erro ao marcar jogo como jogado:', error);
      setError('Não foi possível atualizar o status de jogado. Tente novamente.');
      
      // Mostrar alerta para o usuário em caso de erro
      window.alert('Não foi possível marcar o jogo como jogado. Tente novamente.');
    }
  };

  const handleRemoveGame = async (gameId: string) => {
    if (!user?.id) return;
    if (window.confirm("Tem certeza que deseja remover este jogo? (Isso o removerá de todas as listas)")) {
      try {
        await removeFromCollection(user.id, gameId);
        loadPagedWishlist();
      } catch (error) {
        console.error('Erro ao remover jogo:', error);
        setError('Não foi possível remover o jogo. Tente novamente.');
      }
    }
  };

  const handleUpdatePlayCount = async (gameId: string, playCount: number) => {
    if (!user) return;
    try {
      navigate(`/collection/played?gameId=${gameId}&action=register`);
    } catch (error) {
      console.error('Erro ao navegar para registro de partida:', error);
      setError('Não foi possível acessar o formulário de registro de partida. Tente novamente.');
    }
  };

  const renderDiagnostic = () => {
    if (!isAdmin) return null;
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/70 border border-yellow-200 dark:border-yellow-700 p-4 rounded-lg mb-6">
        <h3 className="font-semibold flex items-center mb-2 text-yellow-800 dark:text-yellow-300"><AlertTriangle className="h-5 w-5 mr-2 text-yellow-600 dark:text-yellow-400" />Diagnóstico (Admin)</h3>
        {diagnosticInfo && <div className="text-xs font-mono overflow-x-auto p-2 bg-black dark:bg-gray-800 text-green-400 dark:text-green-300 rounded">{diagnosticInfo}</div>}
      </div>
    );
  };

  if (!authLoading && !isAuthenticated) {
    return (
      <div className="container mx-auto max-w-6xl py-8">
        <div className="text-center py-16 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">Você precisa estar conectado para ver sua wishlist</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Entre na sua conta para acessar seus jogos desejados.</p>
          <button onClick={() => navigate('/login')} className="bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 text-white font-bold py-2 px-6 rounded-lg inline-flex items-center transition duration-150 ease-in-out">
            <LogIn className="h-5 w-5 mr-2" /> Fazer Login
          </button>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const sortOptions = [
    { value: 'game_name', label: 'Nome' },
    { value: 'ug_rating', label: 'Minha Nota' },
    { value: 'g_average_rating', label: 'Nota Média (Jogo)' },
    { value: 'g_complexity', label: 'Complexidade (Jogo)' },
    { value: 'g_year', label: 'Ano Lançamento (Jogo)' },
    { value: 'ug_updated_at', label: 'Adicionado/Atualizado em' },
  ];

  return (
    <>
      <SEO 
        title="Wishlist"
        description="Acompanhe os jogos de tabuleiro que você deseja adquirir para sua coleção."
      />
      <div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
        {isAdmin && renderDiagnostic()}

        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Minha Wishlist</h1>
          <button onClick={() => navigate('/search')} className="bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg inline-flex items-center transition duration-150 ease-in-out">
            <PlusCircle className="h-5 w-5 mr-2" /> Adicionar Jogos
          </button>
        </div>

        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="search-wishlist" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Buscar na Wishlist</label>
              <div className="relative">
                <input
                  type="text"
                  id="search-wishlist"
                  placeholder="Nome do jogo..."
                  value={searchText}
                  onChange={handleSearchChange}
                  className="w-full p-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
            </div>

            <div>
              <label htmlFor="sort-wishlist" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ordenar por</label>
              <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
                <select
                  id="sort-wishlist"
                  value={sortConfig.sortBy as string}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="w-full p-2 border-none rounded-l-md focus:ring-0 appearance-none bg-transparent text-gray-900 dark:text-gray-100"
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{option.label}</option>
                  ))}
                </select>
                <button onClick={() => handleSortChange(sortConfig.sortBy as string)} className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-r-md">
                  {sortConfig.ascending ? <ArrowUp size={18} /> : <ArrowDown size={18} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando sua wishlist...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/70 p-4 rounded-lg text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700 mb-4">
            <p>{error}</p>
          </div>
        ) : pagedWishlist.length > 0 ? (
          <>
            <div className="gameCard__container grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {pagedWishlist
                .filter(userGame => userGame.game !== null)
                .map(userGame => (
                <GameCard
                  key={userGame.id}
                  game={{
                    ...userGame.game!,
                    categories: userGame.game!.categories || [],
                    mechanics: userGame.game!.mechanics || [],
                    themes: userGame.game!.themes || [],
                    artists: userGame.game!.artists || [],
                    designers: userGame.game!.designers || [],
                  }}
                  userGame={userGame}
                  onToggleOwned={handleToggleOwned}
                  onToggleWishlist={handleToggleWishlist}
                  onTogglePlayed={handleTogglePlayed}
                  onRemove={handleRemoveGame}
                  onUpdatePlayCount={handleUpdatePlayCount}
                  isInitiallyOwned={!!userGame.owned}
                  isInitiallyWishlist={true}
                  isInitiallyPlayed={!!userGame.play_count && userGame.play_count > 0}
                  user={user}
                />
              ))}
            </div>

            <div className="mt-8 flex justify-between items-center">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className={`px-4 py-2 text-sm font-medium rounded-md shadow-sm border border-gray-300 dark:border-gray-600 transition-colors
                ${currentPage <= 1 
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                  : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500'}`}
              >
                Anterior
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Página {currentPage} de {totalPages} (Total: {totalCount})
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className={`px-4 py-2 text-sm font-medium rounded-md shadow-sm border border-gray-300 dark:border-gray-600 transition-colors
                ${currentPage >= totalPages 
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
                  : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500'}`}
              >
                Próxima
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-16 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <Database className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
              {searchText ? 'Nenhum jogo encontrado' : 'Sua wishlist está vazia'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchText
                ? 'Tente ajustar seu termo de busca.'
                : 'Adicione jogos que você deseja para vê-los aqui.'}
            </p>
            {!searchText && (
              <button onClick={() => navigate('/search')} className="bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 text-white font-bold py-2 px-6 rounded-lg inline-flex items-center transition duration-150 ease-in-out">
                <Search className="h-5 w-5 mr-2" /> Encontrar Jogos
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default WishlistPage; 